import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { identity } from '@repletoryreach/sdk-api';
import { Stack, Card, Title, Button, Text, Group, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatDateTime, formatRelative } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

/**
 * The sessions holding a refresh token for this account.
 *
 * `GET /v1/auth/sessions` has existed since the refresh-token table shipped;
 * the page was still telling operators that session enumeration was pending a
 * workstream.
 */
export function SessionsPanel() {
  const t = useT();
  const qc = useQueryClient();

  const sessionsQ = useQuery({
    queryKey: ['admin', 'sessions'],
    queryFn: () => identity.listSessions()
  });

  const revoke = useMutation({
    mutationFn: (id: string) => identity.revokeSession(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'sessions'] })
  });

  const sessions = sessionsQ.data?.data ?? [];

  function confirmRevoke(id: string, device: string) {
    openConfirmModal({
      title: t('settings.security.sessions.revoke.title'),
      children: <p style={{ margin: 0 }}>{t('settings.security.sessions.revoke.body', { device })}</p>,
      labels: { confirm: t('settings.security.sessions.revoke.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await revoke.mutateAsync(id);
          notifySuccess(t('settings.security.sessions.toast.revoked'), t('settings.security.sessions.title'));
        } catch (e) {
          notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.security.sessions.toast.revokeFailed'));
        }
      }
    });
  }

  return (
    <Card withBorder>
      <Stack gap="md">
        <Stack gap="xs">
          <Title order={5}>{t('settings.security.sessions.title')}</Title>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.security.sessions.hint')}</Text>
        </Stack>

        {sessionsQ.isLoading && <Skeleton height={64} />}

        {sessionsQ.error && (
          <Alert color="error">
            <Group justify="space-between" align="center" wrap="wrap">
              <Text role="bodySmall">{t('settings.security.sessions.loadError')}</Text>
              <Button size="xs" variant="outline" onClick={() => void sessionsQ.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        )}

        {!sessionsQ.isLoading && !sessionsQ.error && sessions.length === 0 && (
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.security.sessions.empty')}</Text>
        )}

        {sessions.map((s) => {
          const device = s.user_agent ?? t('settings.security.sessions.unknownDevice');
          return (
            <Group key={s.id} justify="space-between" align="center" wrap="wrap">
              <Stack gap="xs" style={{ minWidth: 0 }}>
                <Text role="bodySmall" lineClamp={1}>{device}</Text>
                <Text role="caption" c="var(--rr-color-text-muted)" title={formatDateTime(s.created_at)}>
                  {t('settings.security.sessions.meta', {
                    started: formatRelative(s.created_at),
                    expires: formatRelative(s.expires_at)
                  })}
                </Text>
              </Stack>
              <Button
                size="xs"
                variant="subtle"
                color="red"
                loading={revoke.isPending && revoke.variables === s.id}
                onClick={() => confirmRevoke(s.id, device)}
              >
                {t('settings.security.sessions.revoke.confirm')}
              </Button>
            </Group>
          );
        })}
      </Stack>
    </Card>
  );
}
